import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError, mediaUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Avatar } from "./Avatar";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tag, X, Check, Trash2, Send, UserPlus, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

export function PhotoViewer({ photo, ownerId, isOwner, canComment, open, onClose, photos = [], onNavigate }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tags, setTags] = useState([]);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [tagQuery, setTagQuery] = useState("");
  const [tagResults, setTagResults] = useState([]);
  const [showTagInput, setShowTagInput] = useState(false);
  const touchX = useRef(null);

  useEffect(() => {
    if (!open || !photo) return;
    setTagQuery(""); setTagResults([]); setShowTagInput(false); setText("");
    api.get(`/photos/${photo.id}/tags`).then(({ data }) => setTags(data)).catch(() => setTags([]));
    api.get(`/photos/${photo.id}/comments`).then(({ data }) => setComments(data)).catch(() => setComments([]));
  }, [open, photo]);

  useEffect(() => {
    if (!tagQuery.trim()) { setTagResults([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/users/search?q=${encodeURIComponent(tagQuery)}`); setTagResults(data); } catch (e) {}
    }, 250);
    return () => clearTimeout(t);
  }, [tagQuery]);

  if (!photo) return null;

  const idx = photos.findIndex((p) => p.id === photo.id);
  const go = (dir) => {
    if (photos.length <= 1 || idx < 0 || !onNavigate) return;
    onNavigate(photos[(idx + dir + photos.length) % photos.length]);
  };
  const onTS = (e) => { touchX.current = e.touches[0].clientX; };
  const onTE = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (dx > 40) go(-1); else if (dx < -40) go(1);
    touchX.current = null;
  };

  const addTag = async (u) => {
    try {
      const { data } = await api.post(`/photos/${photo.id}/tag`, { user_id: u.id });
      setTags((prev) => [...prev, data]); setTagQuery(""); setTagResults([]);
      toast.success(data.status === "pending" ? `${u.name} will be tagged once they approve` : `Tagged ${u.name}`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const respondTag = async (tag, action) => {
    try {
      await api.post(`/photos/tags/${tag.id}/respond`, { action });
      if (action === "approve") setTags((prev) => prev.map((t) => t.id === tag.id ? { ...t, status: "approved" } : t));
      else setTags((prev) => prev.filter((t) => t.id !== tag.id));
      toast.success(action === "approve" ? "Tag approved" : "Tag removed");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const addComment = async () => {
    const t = text.trim(); if (!t) return;
    try { const { data } = await api.post(`/photos/${photo.id}/comments`, { text: t }); setComments((prev) => [...prev, data]); setText(""); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const delComment = async (id) => {
    try { await api.delete(`/photos/comments/${id}`); setComments((prev) => prev.filter((c) => c.id !== id)); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl bg-white border-4 border-black p-0 overflow-hidden" data-testid="photo-viewer">
        <DialogTitle className="sr-only">Photo</DialogTitle>
        <DialogDescription className="sr-only">View photo, tags and comments</DialogDescription>
        <div className="grid md:grid-cols-[1.4fr_1fr] max-h-[88dvh]">
          <div className="relative bg-black flex items-center justify-center" onTouchStart={onTS} onTouchEnd={onTE}>
            <img src={mediaUrl(photo.url)} alt="" draggable={false} className="w-full max-h-[50dvh] md:max-h-[88dvh] object-contain select-none" />
            {photos.length > 1 && (
              <>
                <button data-testid="photo-viewer-prev" onClick={() => go(-1)} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 text-white rounded-full p-2 hover:bg-black"><ChevronLeft className="w-5 h-5" /></button>
                <button data-testid="photo-viewer-next" onClick={() => go(1)} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 text-white rounded-full p-2 hover:bg-black"><ChevronRight className="w-5 h-5" /></button>
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-white text-[11px] font-bold bg-black/60 rounded-full px-2 py-0.5">{idx + 1} / {photos.length}</div>
              </>
            )}
          </div>
          <div className="flex flex-col max-h-[38dvh] md:max-h-[88dvh] bg-spotter-cream">
            <div className="p-3 border-b-2 border-black">
              <div className="flex items-center justify-between mb-1">
                <span className="font-heading font-bold text-spotter-maroon flex items-center gap-1.5 text-sm"><Tag className="w-4 h-4" />Tagged</span>
                {isOwner && (
                  <button data-testid="photo-add-tag-toggle" onClick={() => setShowTagInput((v) => !v)} className="text-xs font-bold text-spotter-maroon inline-flex items-center gap-1 underline"><UserPlus className="w-3.5 h-3.5" />Tag people</button>
                )}
              </div>
              {isOwner && showTagInput && (
                <div className="relative mb-2">
                  <Input data-testid="photo-tag-input" value={tagQuery} onChange={(e) => setTagQuery(e.target.value)} placeholder="Search members to tag…" className="border-2 border-black h-8 text-sm bg-white" />
                  {tagResults.length > 0 && (
                    <div className="absolute z-40 mt-1 w-full bg-white border-2 border-black rounded-lg overflow-hidden brand-shadow-sm">
                      {tagResults.slice(0, 6).map((u) => (
                        <button key={u.id} data-testid={`photo-tag-result-${u.id}`} onClick={() => addTag(u)} className="w-full flex items-center gap-2 p-2 hover:bg-spotter-cream text-left">
                          <Avatar user={u} size="sm" /><span className="text-sm font-semibold">{u.name}</span><span className="text-xs text-spotter-maroon">@{u.username}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {tags.length === 0 ? <p className="text-xs text-spotter-maroon">No one tagged yet.</p> : (
                <div className="flex flex-wrap gap-1.5" data-testid="photo-tags">
                  {tags.map((t) => {
                    const pending = t.status === "pending";
                    const canApprove = pending && t.tagged_user_id === user.id;
                    const canRemove = isOwner || t.tagged_user_id === user.id;
                    return (
                      <span key={t.id} data-testid={`photo-tag-${t.id}`} className={`inline-flex items-center gap-1 border-2 border-black rounded-full pl-1 pr-2 py-0.5 text-xs font-bold ${pending ? "bg-yellow-200" : "bg-spotter-gold/30"}`}>
                        <button onClick={() => navigate(`/profile/${t.tagged_user_id}`)} className="hover:underline">@{t.user?.username}</button>
                        {pending && <span className="text-[9px] uppercase text-spotter-maroon/70">pending</span>}
                        {canApprove && <button data-testid={`photo-tag-approve-${t.id}`} onClick={() => respondTag(t, "approve")} title="Approve"><Check className="w-3 h-3 text-green-700" /></button>}
                        {canRemove && <button data-testid={`photo-tag-remove-${t.id}`} onClick={() => respondTag(t, "remove")} title="Remove"><X className="w-3 h-3" /></button>}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-3" data-testid="photo-comments">
              {comments.length === 0 ? <p className="text-xs text-spotter-maroon">No comments yet.</p> : comments.map((c) => (
                <div key={c.id} className="flex gap-2" data-testid={`photo-comment-${c.id}`}>
                  <Avatar user={c.user} size="sm" />
                  <div className="flex-1 min-w-0">
                    <div className="bg-white border-2 border-black rounded-xl px-3 py-1.5">
                      <button onClick={() => navigate(`/profile/${c.user_id}`)} className="font-bold text-xs hover:underline">{c.user?.name}</button>
                      <p className="text-sm break-words whitespace-pre-wrap">{c.text}</p>
                    </div>
                    <span className="text-[10px] text-spotter-maroon/60">{new Date(c.created_at).toLocaleString()}</span>
                  </div>
                  {(c.user_id === user.id || isOwner) && (
                    <button data-testid={`photo-comment-delete-${c.id}`} onClick={() => delComment(c.id)} className="text-spotter-maroon/60 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /></button>
                  )}
                </div>
              ))}
            </div>
            {canComment ? (
              <div className="p-3 border-t-2 border-black flex gap-2">
                <Input data-testid="photo-comment-input" value={text} onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); addComment(); } }}
                  placeholder="Add a comment…" className="border-2 border-black bg-white" />
                <Button data-testid="photo-comment-send" onClick={addComment} disabled={!text.trim()} className="bg-spotter-maroon text-spotter-cream border-2 border-black"><Send className="w-4 h-4" /></Button>
              </div>
            ) : (
              <div className="p-3 border-t-2 border-black"><p className="text-xs text-spotter-maroon">Add this member to your Pride to comment on their photos.</p></div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
